import type {
  ApiError,
  TimeplanPhaseDto,
  TimeplanPhaseNoteDto,
  CreateTimeplanPhasePayload,
  UpdateTimeplanPhasePayload,
} from '@mesterplan/api-client';
import type {
  TimeplanPhaseModel,
  TimeplanNoteModel,
  CreateTimeplanPhaseData,
  CreateTimeplanBlockerData,
  TimeplanEntryKind,
  TimeplanPhaseStatus,
} from '@mesterplan/shared-types';
import { TRADE_LABELS } from '@mesterplan/shared-types';
import type { AssignmentTradeType } from '@mesterplan/shared-types';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Guid gate: a project keyed by a Guid is .NET-native and its timeplan lives in
 * the authenticated API (`PROJECTS/{guid}/timeplan/phases`); a legacy Firestore
 * push-id project keeps its phases in `PROJECTS/{pushId}/TIMEPLAN_PHASES`. The
 * project-id shape alone decides the source — read AND write the same source. A
 * Guid project's phases are Guid-keyed (the `{phaseId:guid}` routes), so legacy
 * push-id phases can never reach the API regardless; gating on the project id is
 * therefore the only coherent split. No client-side id derivation.
 */
export function isGuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && GUID_PATTERN.test(value);
}

// ── enum wire format (verified against TimeplanPhaseDto + TimeplanPhasesFlowTests) ──
// Both `kind` and `status` are plain string-typed properties on the .NET DTO,
// built by explicit switch/ternary (TimeplanPhaseMapper), so they serialize as
// the exact lowercase/snake literals the TS types declare — `"phase"`/`"blocker"`
// and `"planned"|"ready"|"in_progress"|"completed"`. No integer-enum surprise
// (those only arise for real C# enums; these are strings). The shared-types
// `TimeplanEntryKind`/`TimeplanPhaseStatus` are the same string unions, so the
// mapping below is an identity narrow with a safe fallback.

const ENTRY_KINDS: readonly TimeplanEntryKind[] = ['phase', 'blocker'];
const PHASE_STATUSES: readonly TimeplanPhaseStatus[] = [
  'planned',
  'ready',
  'in_progress',
  'completed',
];

function mapApiKind(kind: string | null | undefined): TimeplanEntryKind {
  // Absence/unknown → 'phase' (the legacy default; absence was treated as phase
  // at read time in the Firestore hook too).
  return (ENTRY_KINDS as readonly string[]).includes(kind ?? '')
    ? (kind as TimeplanEntryKind)
    : 'phase';
}

// The trade slug whitelist is the closed `AssignmentTradeType` union (keys of
// TRADE_LABELS). An unknown/new slug from the API must NOT be propagated as a
// valid model value — it falls back to `undefined` (the UI then renders "other"),
// mirroring the kind/status mappers above.
const TRADE_SLUGS: ReadonlySet<string> = new Set(Object.keys(TRADE_LABELS));

function mapApiTrade(
  trade: string | null | undefined,
): AssignmentTradeType | undefined {
  if (trade == null || trade === '') return undefined;
  if (TRADE_SLUGS.has(trade)) return trade as AssignmentTradeType;
  console.warn(
    `Unmapped timeplan phase trade from API: ${trade} — leaving trade unset`,
  );
  return undefined;
}

function mapApiStatus(
  status: string | null | undefined,
): TimeplanPhaseStatus | undefined {
  if (status == null || status === '') return undefined;
  if ((PHASE_STATUSES as readonly string[]).includes(status)) {
    return status as TimeplanPhaseStatus;
  }
  console.warn(
    `Unmapped timeplan phase status from API: ${status} — leaving status unset`,
  );
  return undefined;
}

/** Dates cross the wire as `yyyy-MM-dd` (DateOnly). Defensive-slice any ISO datetime. */
function toDateOnly(value: string | null | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  return value.length > 10 ? value.slice(0, 10) : value;
}

/**
 * Maps a .NET `TimeplanPhaseDto` to the canonical `TimeplanPhaseModel` (DTO wins).
 * The DTO carries the full modelled field set 1:1 — `companyId` is already the
 * org Guid, `trade` is already the AssignmentTradeType slug, dates are
 * `yyyy-MM-dd`. Optional/absent fields collapse to `undefined` to match the
 * Firestore shape (which omits undefined keys).
 */
export function mapApiTimeplanPhase(dto: TimeplanPhaseDto): TimeplanPhaseModel {
  return {
    id: dto.id,
    projectId: dto.projectId,
    companyId: dto.companyId,
    kind: mapApiKind(dto.kind),
    title: dto.title,
    description: dto.description ?? undefined,
    trade: mapApiTrade(dto.trade),
    estimatedDays: dto.estimatedDays ?? undefined,
    startDate: dto.startDate ?? undefined,
    endDate: dto.endDate ?? undefined,
    partnerInvitationId: dto.partnerInvitationId ?? undefined,
    partnerCompanyId: dto.partnerCompanyId ?? undefined,
    partnerCompanyName: dto.partnerCompanyName ?? undefined,
    color: dto.color ?? undefined,
    sortOrder: dto.sortOrder,
    dependsOn: dto.dependsOn ?? [],
    status: mapApiStatus(dto.status),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    createdBy: dto.createdBy ?? '',
  };
}

/**
 * Maps a .NET `TimeplanPhaseNoteDto` to the canonical `TimeplanNoteModel` (DTO wins). The DTO
 * carries the full modelled field set 1:1, matching the legacy Firestore note document shape.
 */
export function mapApiTimeplanNote(dto: TimeplanPhaseNoteDto): TimeplanNoteModel {
  return {
    id: dto.id,
    phaseId: dto.phaseId,
    authorId: dto.authorId,
    authorName: dto.authorName,
    authorCompanyId: dto.authorCompanyId,
    authorCompanyName: dto.authorCompanyName,
    text: dto.text,
    createdAt: dto.createdAt,
  };
}

/**
 * Builds the .NET create payload for a PHASE. The server owns provenance
 * (companyId from session/X-Active-Org, sortOrder appended, status defaults to
 * "planned", createdAt/updatedAt/createdBy), so those are dropped. Empty-string
 * optionals collapse to undefined (omitted) so the create-time clear-semantics
 * never fire on a net-new entry.
 */
export function toCreatePhasePayload(
  data: CreateTimeplanPhaseData,
): CreateTimeplanPhasePayload {
  return {
    kind: 'phase',
    title: data.title,
    description: data.description ?? undefined,
    trade: data.trade ?? undefined,
    estimatedDays: data.estimatedDays ?? undefined,
    startDate: toDateOnly(data.startDate),
    partnerInvitationId: data.partnerInvitationId ?? undefined,
    partnerCompanyId: data.partnerCompanyId ?? undefined,
    partnerCompanyName: data.partnerCompanyName ?? undefined,
    color: data.color ?? undefined,
    dependsOn: data.dependsOn ?? undefined,
  };
}

/** Builds the .NET create payload for a BLOCKER (kind discriminator + date span). */
export function toCreateBlockerPayload(
  data: CreateTimeplanBlockerData,
): CreateTimeplanPhasePayload {
  return {
    kind: 'blocker',
    title: data.title,
    description: data.description ?? undefined,
    trade: data.trade ?? undefined,
    startDate: toDateOnly(data.startDate),
    endDate: toDateOnly(data.endDate),
  };
}

/**
 * Builds the .NET partial-update payload from a `Partial<TimeplanPhaseModel>`.
 * Mirrors the legacy partial-save semantics exactly: only fields whose value
 * is not `undefined` are forwarded (the old hook filtered `undefined` before the
 * write); an omitted/undefined field leaves the persisted value untouched.
 *
 * The backend `UpdatePhase` writes only non-null arguments, and for the four
 * free-text fields (description/trade/color/partnerCompanyName) treats an empty
 * string as "clear to null". So a model value of `''`/`null` on those maps to a
 * wire empty string (clear); for the value-typed fields (dates/estimatedDays/
 * status/dependsOn/partner*Id) a `null` value maps to `null` = untouched, since
 * those have no "clear" wire path (this matches the Firestore behaviour, which
 * also never blanked them).
 *
 * `status` is narrowed to the wire union (the model uses the same literals).
 * Date fields are sliced to `yyyy-MM-dd` for the DateOnly bind. `sortOrder`,
 * `companyId`, `projectId`, `createdAt`/`updatedAt`/`createdBy` and `kind` are
 * not on the update wire (reorder owns sortOrder; the rest are immutable/
 * server-owned), so they are intentionally not forwarded.
 */
export function toUpdatePhasePayload(
  data: Partial<TimeplanPhaseModel>,
): UpdateTimeplanPhasePayload {
  const payload: UpdateTimeplanPhasePayload = {};
  // Free-text fields: empty/clear → '' (clears server-side).
  if (data.title !== undefined) payload.title = data.title;
  if (data.description !== undefined) payload.description = data.description ?? '';
  if (data.trade !== undefined) payload.trade = data.trade ?? '';
  if (data.color !== undefined) payload.color = data.color ?? '';
  if (data.partnerCompanyName !== undefined)
    payload.partnerCompanyName = data.partnerCompanyName ?? '';
  // Value/Guid fields: null = untouched (no clear path; parity with Firestore).
  if (data.estimatedDays !== undefined)
    payload.estimatedDays = data.estimatedDays ?? null;
  if (data.startDate !== undefined)
    payload.startDate = toDateOnly(data.startDate) ?? null;
  if (data.endDate !== undefined) payload.endDate = toDateOnly(data.endDate) ?? null;
  if (data.status !== undefined) payload.status = data.status ?? null;
  if (data.partnerInvitationId !== undefined)
    payload.partnerInvitationId = data.partnerInvitationId ?? null;
  if (data.partnerCompanyId !== undefined)
    payload.partnerCompanyId = data.partnerCompanyId ?? null;
  // dependsOn: backend `UpdatePhase` does `if (blockers is not null) SetBlockers(blockers)`,
  // so a non-null array (incl. `[]`) REPLACES the set (an empty array clears it) and `null`
  // leaves it untouched. We send the array as-is, mapping an absent value to `null` = untouched.
  if (data.dependsOn !== undefined) payload.dependsOn = data.dependsOn ?? null;
  return payload;
}

/**
 * Resolves a user-facing message from a problem-details `ApiError`, preferring the
 * specific `detail`, then the `title`, then a caller-supplied fallback. Centralised so
 * the timeplan write paths (create/update/delete/reorder) don't drift on error wording.
 */
export function formatApiError(
  error: ApiError | undefined,
  fallback: string,
): string {
  return error?.detail ?? error?.title ?? fallback;
}
